/**
 * 打印机路由、订单 dispatch、本地 thermal POST、printerSettings。须在 app.js 之前加载。
 * 由 app.js 调用 global.__OS_createPrintModule(deps) 注入存储与菜单等依赖。
 */
(function (global) {
  'use strict';

  global.__OS_createPrintModule = function createPrintModule(deps) {
    const {
      KEYS,
      getFromDB,
      saveToDB,
      getMenu,
      getPrintConfig,
      getPrinterRouting,
      getMergedReceiptConfig,
      UI_LANG_ALLOWED,
      GUEST_NAME_MAX_LEN,
      receipt: R,
    } = deps;

    function fallbackPrintEndpoint() {
      try {
        const RC = global.RestaurantConstants;
        if (RC && RC.OS_LAN_IPV4) {
          const p = RC.DEFAULT_HTTP_PORT != null ? Number(RC.DEFAULT_HTTP_PORT) : 3000;
          return 'http://' + String(RC.OS_LAN_IPV4).trim() + ':' + p + '/print';
        }
      } catch (_) {}
      return 'http://192.168.100.112:3000/print';
    }

    const defaultPrintEndpoint =
      deps.defaultPrintEndpoint != null && String(deps.defaultPrintEndpoint).trim()
        ? String(deps.defaultPrintEndpoint).trim().replace(/\/$/, '')
        : fallbackPrintEndpoint();

    function dishCategoryFromMenu(menu, dishId) {
      const idStr = dishId != null ? String(dishId) : '';
      const d = (menu.dishes || []).find((x) => String(x.id) === idStr || x.id === dishId);
      return d && d.category != null ? String(d.category) : '';
    }

    /**
     * 按打印机路由与出单模式，将订单拆成任务组。
     * @returns {{ mode: string, orderId: string, tasks: Array<{type:string, printerId:string, categoryId:string, items:object[], meta:object}> }}
     */
    function dispatchOrder(orderData) {
      const menu = getMenu();
      const printCfg = getPrintConfig();
      const routing = getPrinterRouting();
      const printers = printCfg.printers || [];
      const defaultPid =
        (printCfg.activePrinterId && printers.some((p) => p.id === printCfg.activePrinterId)
          ? printCfg.activePrinterId
          : null) || (printers[0] && printers[0].id) ||
        'p_default';

      function resolvePrinterId(categoryId) {
        const mapped = routing.categoryToPrinterId[categoryId];
        if (mapped && printers.some((p) => p.id === mapped)) return mapped;
        return defaultPid;
      }

      const items = Array.isArray(orderData.items) ? orderData.items : [];
      const enriched = items.map((it) => {
        const categoryId = dishCategoryFromMenu(menu, it.dishId != null ? it.dishId : it.id);
        return { ...it, categoryId };
      });

      const mode = routing.dispatchMode === 'per_dish' ? 'per_dish' : 'per_category';
      const tasks = [];

      function thermalItemPayload(it) {
        const o = {
          dishId: it.dishId != null ? it.dishId : it.id,
          name: it.name,
          price: it.price,
          quantity: it.quantity != null ? it.quantity : it.q || 1,
        };
        if (it.thermalPrinter != null && String(it.thermalPrinter).trim() !== '') {
          o.thermalPrinter = String(it.thermalPrinter).trim();
        }
        return o;
      }

      if (mode === 'per_dish') {
        enriched.forEach((it, idx) => {
          const pid = resolvePrinterId(it.categoryId);
          tasks.push({
            type: 'single_dish',
            printerId: pid,
            categoryId: it.categoryId,
            items: [thermalItemPayload(it)],
            meta: { orderId: orderData.id, lineIndex: idx },
          });
        });
      } else {
        const byCat = new Map();
        enriched.forEach((it) => {
          const key = it.categoryId || '_uncat';
          if (!byCat.has(key)) byCat.set(key, []);
          byCat.get(key).push(it);
        });
        byCat.forEach((groupItems, catKey) => {
          const categoryId = catKey === '_uncat' ? '' : catKey;
          const pid = resolvePrinterId(categoryId);
          tasks.push({
            type: 'category_batch',
            printerId: pid,
            categoryId,
            items: groupItems.map((it) => thermalItemPayload(it)),
            meta: { orderId: orderData.id },
          });
        });
      }

      return {
        mode,
        orderId: orderData.id,
        tasks,
        orderSnapshot: {
          id: orderData.id,
          at: orderData.at,
          restaurantName: orderData.restaurantName,
          table: orderData.table,
          guestCount: orderData.guestCount,
          guestName: orderData.guestName,
          total: orderData.total,
        },
      };
    }

    function fetchPostWithTimeout(url, body, timeoutMs) {
      const ms = timeoutMs > 0 ? timeoutMs : 12000;
      return new Promise((resolve, reject) => {
        let settled = false;
        const t = setTimeout(() => {
          if (settled) return;
          settled = true;
          reject(new Error('print-dispatch timeout'));
        }, ms);
        global
          .fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body,
          })
          .then((res) => {
            if (settled) return;
            settled = true;
            clearTimeout(t);
            resolve(res);
          })
          .catch((err) => {
            if (settled) return;
            settled = true;
            clearTimeout(t);
            reject(err);
          });
      });
    }

    function postPrintDispatch(dispatchPayload, baseUrl) {
      const u = String(baseUrl || '').trim().replace(/\/$/, '');
      if (!u) return Promise.resolve({ ok: false, skipped: true, message: 'no printProxyUrl' });
      const url = u + '/api/print-dispatch';
      return fetchPostWithTimeout(url, JSON.stringify(dispatchPayload), 12000)
        .then((res) =>
          res
            .json()
            .catch(() => ({}))
            .then((body) => ({ ok: res.ok, status: res.status, body }))
        )
        .catch((e) => ({ ok: false, error: String(e.message || e) }));
    }

    const DEFAULT_PRINTER_SETTINGS = {
      printMode: 'summary',
      categoryToPrinter: {},
      categoryPrintMode: {},
      defaultPrinterName: 'default',
      printEndpoint: defaultPrintEndpoint,
      enabled: true,
    };

    function normalizePrinterSettings(raw) {
      const base = JSON.parse(JSON.stringify(DEFAULT_PRINTER_SETTINGS));
      if (!raw || typeof raw !== 'object') return base;
      if (raw.printMode === 'single' || raw.printMode === 'summary') base.printMode = raw.printMode;
      if (raw.defaultPrinterName != null && String(raw.defaultPrinterName).trim()) {
        base.defaultPrinterName = String(raw.defaultPrinterName).trim();
      }
      if (raw.printEndpoint != null && String(raw.printEndpoint).trim()) {
        base.printEndpoint = String(raw.printEndpoint).trim().replace(/\/$/, '');
      }
      if (raw.enabled === false) base.enabled = false;
      const src = raw.categoryToPrinter || raw.categories;
      if (src && typeof src === 'object') {
        Object.keys(src).forEach((k) => {
          const v = src[k];
          if (v != null && String(v).trim()) base.categoryToPrinter[k] = String(v).trim();
        });
      }
      if (raw.categoryPrintMode && typeof raw.categoryPrintMode === 'object') {
        Object.keys(raw.categoryPrintMode).forEach((k) => {
          const v = raw.categoryPrintMode[k];
          if (v === 'single' || v === 'summary') base.categoryPrintMode[k] = v;
        });
      }
      return base;
    }

    function getPrinterSettings() {
      const d = getFromDB(KEYS.PRINTER_SETTINGS, null);
      return normalizePrinterSettings(d && typeof d === 'object' ? d : null);
    }

    function savePrinterSettings(partial) {
      if (!partial || typeof partial !== 'object') return getPrinterSettings();
      const cur = getPrinterSettings();
      const next = {
        ...cur,
        printMode:
          partial.printMode === 'single' || partial.printMode === 'summary'
            ? partial.printMode
            : cur.printMode,
        defaultPrinterName:
          partial.defaultPrinterName != null ? String(partial.defaultPrinterName).trim() : cur.defaultPrinterName,
        printEndpoint:
          partial.printEndpoint != null ? String(partial.printEndpoint).trim().replace(/\/$/, '') : cur.printEndpoint,
        enabled: partial.enabled === false ? false : partial.enabled === true ? true : cur.enabled,
        categoryToPrinter: { ...cur.categoryToPrinter },
        categoryPrintMode: { ...cur.categoryPrintMode },
      };
      const src = partial.categoryToPrinter || partial.categories;
      if (partial.replaceCategories === true && src && typeof src === 'object') {
        next.categoryToPrinter = {};
        Object.keys(src).forEach((k) => {
          const v = src[k];
          if (v != null && String(v).trim()) next.categoryToPrinter[k] = String(v).trim();
        });
      } else if (src && typeof src === 'object') {
        Object.keys(src).forEach((k) => {
          const v = src[k];
          if (v == null || !String(v).trim()) delete next.categoryToPrinter[k];
          else next.categoryToPrinter[k] = String(v).trim();
        });
      }
      if (partial.categoryPrintMode && typeof partial.categoryPrintMode === 'object') {
        Object.keys(partial.categoryPrintMode).forEach((k) => {
          const v = partial.categoryPrintMode[k];
          if (v === 'single' || v === 'summary') next.categoryPrintMode[k] = v;
          else delete next.categoryPrintMode[k];
        });
      }
      const finalized = normalizePrinterSettings(next);
      saveToDB(KEYS.PRINTER_SETTINGS, finalized);
      return finalized;
    }

    function categoryLabelFromMenu(menu, categoryId) {
      if (!categoryId) return '其他';
      const c = (menu.categories || []).find((x) => x.id === categoryId);
      return c ? String(c.name || c.id) : String(categoryId);
    }

    function normalizeOrderItemForPrint(it) {
      return {
        dishId: it.dishId != null ? it.dishId : it.id,
        name: it.name,
        price: it.price,
        quantity: it.quantity != null ? it.quantity : it.q || 1,
      };
    }

    function enrichOrderItemForLocalThermal(it, receiptCfg) {
      const menu = getMenu();
      const id = it.dishId != null ? it.dishId : it.id;
      const r1 = UI_LANG_ALLOWED.includes(receiptCfg.receiptLang) ? receiptCfg.receiptLang : 'de';
      const r2 = R.normalizeReceiptLangSecondary(r1, receiptCfg.receiptLangSecondary);
      const n1 = R.dishNameForReceiptLang(menu, id, r1, it.name);
      const base = normalizeOrderItemForPrint(it);
      base.name = n1;
      if (r2) {
        const n2 = R.dishNameForReceiptLang(menu, id, r2, it.name);
        if (n2 !== n1) base.nameSecondary = n2;
      }
      return base;
    }

    function extendContentForThermalPrint(content, receiptCfg) {
      const orderLike = {
        table: content.table,
        guestName: content.guestName,
        guestCount: content.guestCount,
        items: content.items,
        at: content.at,
      };
      return {
        ...content,
        receiptPrint: R.buildReceiptPrintPayload(receiptCfg),
        receiptCompact: R.buildCompactReceiptPayload(orderLike, receiptCfg),
      };
    }

    function buildLocalPrintContent(order, printMode, categoryId, items, extra) {
      const menu = getMenu();
      const orPrint = order.orderRound != null ? parseInt(String(order.orderRound), 10) : NaN;
      return {
        orderId: order.id,
        at: order.at,
        restaurantName: order.restaurantName,
        table: order.table,
        guestCount: order.guestCount,
        guestName:
          order.guestName != null && String(order.guestName).trim()
            ? String(order.guestName).trim().slice(0, GUEST_NAME_MAX_LEN)
            : '',
        total: order.total,
        printMode,
        categoryId: categoryId || null,
        categoryLabel: categoryLabelFromMenu(menu, categoryId),
        items,
        ...(Number.isFinite(orPrint) && orPrint > 0 ? { orderRound: orPrint } : {}),
        ...extra,
      };
    }

    function resolvePrinterNameForLocalPrint(settings, categoryId) {
      const map = settings.categoryToPrinter || {};
      const cid = categoryId || '';
      if (cid && map[cid] != null && String(map[cid]).trim()) return String(map[cid]).trim();
      return settings.defaultPrinterName ? String(settings.defaultPrinterName).trim() : 'default';
    }

    /** 菜品上 thermalPrinter 优先（与菜单管理「打印机」一致），否则按分类/默认。 */
    function resolvePrinterNameForItem(settings, categoryId, item) {
      if (item && item.thermalPrinter != null && String(item.thermalPrinter).trim() !== '') {
        return String(item.thermalPrinter).trim();
      }
      return resolvePrinterNameForLocalPrint(settings, categoryId);
    }

    function resolveCategoryPrintMode(settings, categoryId) {
      const cpm = settings.categoryPrintMode && typeof settings.categoryPrintMode === 'object' ? settings.categoryPrintMode : {};
      const cid = categoryId || '';
      if (cid && (cpm[cid] === 'single' || cpm[cid] === 'summary')) return cpm[cid];
      if (cpm.default === 'single' || cpm.default === 'summary') return cpm.default;
      if (settings.printMode === 'single' || settings.printMode === 'summary') return settings.printMode;
      return 'summary';
    }

    /**
     * 前端订单分发：读 localStorage.printerSettings，按 single/summary 拆任务，POST 到 printEndpoint。
     * @returns {Promise<{ endpoint: string, results: PromiseSettledResult[], tasks: { printerName: string, content: object }[] }>}
     */
    function processOrderPrinting(order) {
      const settings = getPrinterSettings();
      const endpoint = settings.printEndpoint || defaultPrintEndpoint;

      if (!settings.enabled) {
        return Promise.resolve({
          skipped: true,
          endpoint,
          tasks: [],
          results: [],
        });
      }

      const menu = getMenu();
      const receiptCfg = getMergedReceiptConfig(getPrintConfig(), null);
      const items = Array.isArray(order.items) ? order.items : [];
      const enriched = items.map((it) => ({
        ...it,
        categoryId: dishCategoryFromMenu(menu, it.dishId != null ? it.dishId : it.id),
      }));

      const tasks = [];
      const groups = new Map();
      enriched.forEach((it) => {
        const catKey = it.categoryId || '_uncat';
        const categoryId = catKey === '_uncat' ? '' : catKey;
        const mode = resolveCategoryPrintMode(settings, categoryId);
        const printerName = resolvePrinterNameForItem(settings, categoryId, it);
        const gkey = printerName + '\0' + catKey + '\0' + mode;
        if (!groups.has(gkey)) {
          groups.set(gkey, { printerName, categoryId, mode, items: [] });
        }
        groups.get(gkey).items.push(it);
      });
      let lineIndex = 0;
      groups.forEach((g) => {
        if (g.mode === 'single') {
          g.items.forEach((it) => {
            tasks.push({
              printerName: g.printerName,
              content: extendContentForThermalPrint(
                buildLocalPrintContent(
                  order,
                  'single',
                  g.categoryId,
                  [enrichOrderItemForLocalThermal(it, receiptCfg)],
                  { lineIndex: lineIndex++ }
                ),
                receiptCfg
              ),
            });
          });
        } else {
          tasks.push({
            printerName: g.printerName,
            content: extendContentForThermalPrint(
              buildLocalPrintContent(
                order,
                'summary',
                g.categoryId,
                g.items.map((it) => enrichOrderItemForLocalThermal(it, receiptCfg)),
                {}
              ),
              receiptCfg
            ),
          });
        }
      });

      if (!tasks.length) {
        return Promise.resolve({ skipped: false, endpoint, tasks: [], results: [] });
      }

      const results = tasks.map((t) =>
        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ printerName: t.printerName, content: t.content }),
        }).then(async (res) => {
          const text = await res.text();
          let body;
          try {
            body = text ? JSON.parse(text) : {};
          } catch (_e) {
            body = { raw: text };
          }
          if (!res.ok) {
            const err = new Error('HTTP ' + res.status + ': ' + text.slice(0, 200));
            err.status = res.status;
            err.body = body;
            throw err;
          }
          return { status: res.status, body };
        })
      );

      return Promise.all(
        results.map((p) =>
          Promise.resolve(p).then(
            (value) => ({ status: 'fulfilled', value }),
            (reason) => ({ status: 'rejected', reason })
          )
        )
      ).then((settled) => ({
        endpoint,
        tasks,
        results: settled,
      }));
    }

    return {
      dishCategoryFromMenu,
      dispatchOrder,
      postPrintDispatch,
      DEFAULT_PRINTER_SETTINGS,
      normalizePrinterSettings,
      getPrinterSettings,
      savePrinterSettings,
      categoryLabelFromMenu,
      normalizeOrderItemForPrint,
      enrichOrderItemForLocalThermal,
      extendContentForThermalPrint,
      buildLocalPrintContent,
      resolvePrinterNameForLocalPrint,
      resolveCategoryPrintMode,
      processOrderPrinting,
    };
  };
})(typeof window !== 'undefined' ? window : globalThis);
